import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Img, Text} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {getDamImagePicture} from '../../../custom-hooks/use-dam-image'
import {ChevronRightIcon} from '../../icons'

const IMAGE_OPTIONS = {objectFit: 'cover', mixBlendMode: 'multiply'}

const BentoboxCard = ({card, styles, inside, dominant, localeId, categoriesData}) => {
    const showImageBlur = inside && card.blur_overlay !== false
    const bodyCopy = card.body_copy ?? card.subtitle
    const authoredLink = card.link || card.redirect || card.button
    const directUrl = card.url || (typeof authoredLink === 'string' ? authoredLink : null)
    const {url, isAbsolute, label} = getGlobalLink({
        link: directUrl ? {cta: {href: directUrl}} : authoredLink,
        categoriesData,
        locale: localeId,
    })
    const authoredUrl = directUrl || authoredLink?.cta?.href
    const safeScheme =
        !authoredUrl ||
        (Array.from(authoredUrl).every((character) => character.charCodeAt(0) > 32) &&
            (!/^[a-z][a-z\d+.-]*:/i.test(authoredUrl) || /^https?:\/\//i.test(authoredUrl)))
    const validUrl = safeScheme ? url : null
    const linkProps = validUrl
        ? isAbsolute
            ? {as: 'a', href: validUrl, target: '_blank', rel: 'noopener noreferrer'}
            : {as: Link, to: validUrl}
        : {}
    const body = (card.title || bodyCopy) && (
        <Flex
            {...styles?.body}
            {...(inside ? styles?.insideBody : styles?.outsideBody)}
            {...(inside && card.blur_overlay ? styles?.textBackdrop : {})}
            data-testid="bentobox-body"
        >
            {card.title && (
                <Text as="h3" {...styles?.cardTitle} {...(!inside ? styles?.outsideText : {})}>
                    {card.title}
                </Text>
            )}
            {bodyCopy && (
                <Text {...styles?.bodyCopy} {...(!inside ? styles?.outsideCopy : {})}>
                    {bodyCopy}
                </Text>
            )}
        </Flex>
    )

    return (
        <Box
            {...styles?.card}
            {...(dominant ? styles?.dominantCard : {})}
            {...linkProps}
            sx={styles?.interactive}
            data-testid="bentobox-card"
            data-text-position={inside ? 'inside' : 'outside'}
        >
            <Box {...styles?.media} data-testid="bentobox-media">
                {card.image && (
                    <Box {...styles?.image} data-testid="bentobox-image">
                        {getDamImagePicture(card.image, card.title || '', false, 1, IMAGE_OPTIONS)}
                    </Box>
                )}
                {card.image && showImageBlur && (
                    <Box {...styles?.blurClip} aria-hidden="true" data-testid="bentobox-blur-clip">
                        <Box {...styles?.blurImage} data-testid="bentobox-blur-image">
                            {getDamImagePicture(card.image, '', false, 1, IMAGE_OPTIONS)}
                        </Box>
                    </Box>
                )}
                {card.icon?.url && (
                    <Flex {...styles?.iconAlignment} aria-hidden="true">
                        <Box {...styles?.iconContainer}>
                            <Img
                                {...styles?.icon}
                                src={card.icon.url}
                                alt=""
                                data-testid="bentobox-icon"
                            />
                        </Box>
                    </Flex>
                )}
                {((inside && body) || validUrl) && (
                    <Flex {...styles?.overlay}>
                        {inside && body}
                        {validUrl && (
                            <Flex
                                as="span"
                                {...styles?.cta}
                                aria-hidden="true"
                                title={label || card.title}
                                data-testid="bentobox-cta"
                            >
                                <ChevronRightIcon {...styles?.ctaIcon} />
                            </Flex>
                        )}
                    </Flex>
                )}
            </Box>
            {!inside && body}
        </Box>
    )
}

BentoboxCard.propTypes = {
    card: PropTypes.object.isRequired,
    styles: PropTypes.object.isRequired,
    inside: PropTypes.bool.isRequired,
    dominant: PropTypes.bool,
    localeId: PropTypes.string,
    categoriesData: PropTypes.object,
}

export default BentoboxCard
